// Unpublished Work © 2023-2026 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {connect} from 'react-redux';
import {IconEdit} from '@deere/icons';
import {DatePicker, MultiSelect} from '@deere/isg.component-library';
import Stack from '@mui/material/Stack';
import LoadingWrapper from 'Ui/components/common/loading-wrapper';
import OnLinkIconButton from 'Ui/components/common/onlink-icon-button';
import TodoList from 'OnLabor/workboard/common/todo-list';
import WorkBoardWeather from 'OnLabor/my-work/work-board-tile/work-board-weather';
import {updateWorkboardLayers} from 'Store/actions/workboard';
import {useDeepMemo} from 'Utils/react-utils';
import {DATE_FORMATS, formatLocalizedTime, WEEKDAY_AND_NUMERIC_DATE_OPTIONS} from 'Utils/time-utils';
import {getWeatherIcon} from 'Utils/weather-utils';
import moment from 'moment';
import {MANAGE_TIMEOFF} from 'Common/constants/business-activities';
import {useHistory} from 'react-router-dom';
import {PRO_GOLF_LABOR_RESKIN} from 'Common/constants/isg-toggles';

const ICON_EDIT_STYLE = {
    style: {
        display: 'inline',
        height: '20px',
        marginRight: '5px',
        width: '20px'
    }
};

function onLayersSelectionChange(selectedIds, updateLayersState, appUserId) {
    const newLayers = selectedIds.reduce((layersSelected, selectedId) => {
        layersSelected[selectedId] = true;

        return layersSelected;
    }, {});

    updateLayersState(newLayers);

    if (selectedIds.includes('showTimeOff')) {
        localStorage.setItem(`${appUserId}_showTimeOff`, 'true');
    } else {
        localStorage.setItem(`${appUserId}_showTimeOff`, 'false');
    }
}

function getWorkboardsForMobile(
    dateValue,
    fetchWorkboards,
    layers,
    openAddWorkboardDialog,
    workboards,
    laborRefresh,
    history
) {
    const filteredWorkboards = workboards.filter((workboard) => workboard.dateSelect === dateValue);

    return layers.showWorkboards && filteredWorkboards?.length > 0 && (
        <>
            <div
                className='title-with-underline'
                data-testid='title-with-underline'
            />
            {
                filteredWorkboards.map((workboardData) => (
                    <div
                        className='sidebar-group horizontal'
                        key={`workboard-${workboardData.id}`}
                    >
                        <span
                            className='workboard-item'
                            data-testid='workboard-item'
                        >
                            {workboardData.title}
                        </span>
                        <OnLinkIconButton
                            onClick={() =>
                                laborRefresh ? history.push(`/labor/workboard/${workboardData.id}`) :
                                    openAddWorkboardDialog({
                                        onClose: fetchWorkboards,
                                        workboards,
                                        workboardId: workboardData.id
                                    })
                            }
                        >
                            <IconEdit
                                {...ICON_EDIT_STYLE}
                                primary={'#333'}
                            />
                        </OnLinkIconButton>
                    </div>
                ))
            }
        </>
    );
}

function getTimeOffLayerData(
    dateValue,
    layers,
    availabilityByDate
) {
    const filteredUsers = availabilityByDate.filter((user) => user.start === dateValue);

    return layers.showTimeOff && filteredUsers?.length > 0 && (
        <>
            <div
                className='title-with-underline'
                data-testid='title-with-underline'
            />
            {
                filteredUsers.map((availability) => (
                    <span
                        className='workboard-item'
                        data-testid='workboard-item'
                        key={availability.title}
                    >
                        {availability.title}
                    </span>
                ))
            }
        </>
    );
}

function findWeatherForTheDay(formattedDate, weather) {
    const selectedDateWeather = weather?.find((dayData) =>
        dayData?.forecast?.fcst_valid_local && moment(dayData.forecast.fcst_valid_local).format(DATE_FORMATS.day) === formattedDate);

    if (selectedDateWeather?.forecast) {
        return (
            <div
                className='work-board-weather'
                data-testid='work-board-weather'
            >
                <img
                    alt={selectedDateWeather.forecast.icon_code.toString()}
                    className='workboard-weather-icon'
                    src={getWeatherIcon(selectedDateWeather.forecast.icon_code)}
                />
                {selectedDateWeather.title}
            </div>
        );
    }

    return null;
}

function getCalendarLayerToggles(translations, isMigrated, myJdPermissions) {
    const calendarLayerToggles = [
        {
            title: translations.ONLINK_WORKBOARDS,
            id: 'showWorkboards'
        }
    ];

    if (!isMigrated || myJdPermissions[MANAGE_TIMEOFF]) {
        calendarLayerToggles.push({
            title: translations.ONLINK_TIME_OFF,
            id: 'showTimeOff'
        });
    }

    return calendarLayerToggles;
}

function CalendarMobile(props) {
    const {
        appUserId,
        availabilityByDate,
        dateValue,
        fetchWorkboards,
        isgToggles,
        isMigrated,
        layers,
        loading,
        membershipId,
        myJdPermissions,
        openAddWorkboardDialog,
        setDateValue,
        setStartAndEndTime,
        todoTemplates,
        translations,
        updateLayersState,
        weather,
        weatherRef,
        workboards
    } = props;

    const history = useHistory();
    const dateMoment = moment(dateValue);
    const formattedDateValue = dateMoment.format(DATE_FORMATS.day);

    const selectedLayers = useDeepMemo(() => {
        return Object.entries(layers)
            .filter(([, enabled]) => enabled === true)
            .map(([layerId]) => layerId);
    }, [layers]);

    React.useEffect(() => {
        const showTimeOff = localStorage.getItem(`${appUserId}_showTimeOff`) === 'true';

        if (showTimeOff) {
            onLayersSelectionChange(['showTimeOff', ...selectedLayers], updateLayersState, appUserId);
        }
    }, []);

    return (
        <Stack
            className='workboard-container'
            direction='column'
            flex={1}
            paddingX='12px'
            sx={{
                overflowX: 'hidden',
                overflowY: 'auto'
            }}
        >
            <Stack
                alignItems='flex-start'
                className='mobile-group'
                direction='row'
                justifyContent='space-between'
                padding='5px'
            >
                <MultiSelect
                    allItemsLabel={translations.ONLINK_ALL_LAYERS}
                    className='multiselect hide-all-option layers-multiselect'
                    items={getCalendarLayerToggles(translations, isMigrated, myJdPermissions)}
                    labels={{
                        multiSelect: translations.ONLINK_LAYERS,
                        placeholder: translations.ONLINK_SELECT_LAYER,
                        emptyList: translations.NO_ITEMS,
                        allSelect: translations.ALL_ITEMS_SELECTED,
                        selectAllItems: translations.SELECT_ALL
                    }}
                    noSearch={true}
                    onChange={(selectedIds) => onLayersSelectionChange(selectedIds, updateLayersState, appUserId)}
                    selectedIds={selectedLayers}
                />
            </Stack>
            <DatePicker
                inline={true}
                onChange={(selectedDate) => {
                    setDateValue(selectedDate);

                    const formattedDate = moment(selectedDate).format(DATE_FORMATS.day);

                    setStartAndEndTime({
                        start: formattedDate,
                        end: formattedDate
                    });
                }}
                value={dateValue}
            />
            <LoadingWrapper
                className='table-loading-icon'
                loading={loading.workboards || loading.availability}
                size='50px'
            >
                <div className='sidebar-group horizontal mobile-group weather'>
                    {formatLocalizedTime(dateMoment, WEEKDAY_AND_NUMERIC_DATE_OPTIONS)}
                    {findWeatherForTheDay(formattedDateValue, weather)}
                </div>
                {
                    dateMoment.isSame(new Date(), 'day') && weatherRef.current &&
                    <WorkBoardWeather
                        date={formattedDateValue}
                        weather={weatherRef.current}
                    />
                }
                {getWorkboardsForMobile(formattedDateValue, fetchWorkboards, layers, openAddWorkboardDialog, workboards, isgToggles[PRO_GOLF_LABOR_RESKIN], history)}
                {getTimeOffLayerData(formattedDateValue, layers, availabilityByDate)}
            </LoadingWrapper>
            <TodoList
                appUserId={appUserId}
                membershipId={membershipId}
                todoTemplates={todoTemplates}
                translations={translations}
            />
        </Stack>
    );
}

CalendarMobile.propTypes = {
    appUserId: PropTypes.string,
    availabilityByDate: PropTypes.arrayOf(PropTypes.object),
    dateValue: PropTypes.instanceOf(Date),
    fetchWorkboards: PropTypes.func,
    isgToggles: PropTypes.object,
    isMigrated: PropTypes.bool,
    layers: PropTypes.object,
    loading: PropTypes.object,
    membershipId: PropTypes.string,
    myJdPermissions: PropTypes.myJdPermissions,
    openAddWorkboardDialog: PropTypes.func,
    setDateValue: PropTypes.func,
    setStartAndEndTime: PropTypes.func,
    todoTemplates: PropTypes.arrayOf(PropTypes.object),
    translations: PropTypes.translations,
    updateLayersState: PropTypes.func,
    weather: PropTypes.arrayOf(PropTypes.object),
    weatherRef: PropTypes.reference,
    workboards: PropTypes.arrayOf(PropTypes.object)
};

export function mapStateToProps(state) {
    return {
        appUserId: state.account.appUserId,
        isgToggles: state.account.isgToggles,
        isMigrated: state.membership.isMigrated,
        myJdPermissions: state.account.myJdPermissions
    };
}

export function mapDispatchToProps(dispatch) {
    return {
        updateLayersState(props) {
            dispatch(updateWorkboardLayers(props));
        }
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(CalendarMobile);
